import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  IconButton,
  IconMoreVert,
  Table,
  TABLE_LAYOUT_OPTIONS,
  IconStatusWarningExclamation,
  Menu,
  MenuListItem,
  TextButton,
  IconEdit,
  IconVisibility,
  IconRemove,
  IconAdd,
  IconDelete,
  withTooltip,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS,
  THEMES,
  IconSales,
} from 'cdk-radial';
import WebhookWarningDialog from './WebhookWarningDialog';
import styled from 'styled-components';
import './APIs.css';
import classNames from 'classnames';
import {
  API_TYPES,
  API_TYPES_OPTIONS,
  DUMMY_WEBHOOK_URL,
} from '../../../../../constants/APIDirectoryConstants';
import {
  getAPIDetailsById,
  setAPIDirectoryPage,
} from '../../../../../redux/connectedComponent/apiDirectory/apiDirectory.slice';
import { APITerms } from './APITerms';
import { APIWebhook } from './APIWebhook';
import {
  storeActiveApiList,
} from '../../../../../redux/connectedComponent/orderAPI/orderAPI.slice';
import {
  getMatchingResources,
  hasIntegrationCertified,
} from '../../../../../utils/utils';
import { clearSubscriptions } from '../../../../../redux/connectedComponent/daabSubscriptions/daabSubscriptions.slice';
import { APIPricingPlan } from './APIPricingPlan';
import { groupBy } from 'lodash';
import { setScreen } from '../../../../../redux/connectedComponent/uiFactory/uiFactory.slice';
import { screens } from '../../../../config/uiFactory.config';
import { NoDataOverlay } from '../../../utils/NoDataOverlay';
import {  PAGE_TITLE, RESOURCE_TYPES } from '../../../../../constants/constants';
import { APIDelete } from './APIDelete';

import {
  getOrgAmplitudeProperties,
  sendAmplitudeData,
} from '../../../../../utils/amplitude';
import { trackManageApiIntegration, getFormattedApiType } from '../../../../../utils/gtm';
import { AMPLITUDE_EVENTS } from '../../../../../constants/amplitudeConstants';

const NA = 'N/A';
const WEBHOOK_BUTTON_TEXT = 'Webhook';
const API_DIR_URL = '/dp/api-directory';
const StyledIconStatusWarningExclamation = styled(IconStatusWarningExclamation)`
  fill: ${({ theme }) =>
    theme.color.secondary.cherryBombRed[500].value} !important;
`;

export const WebhookRender = ({ data, isLoadingTableData }) => {
  const [showWebhook, setShowWebhook] = useState(false);
  const { type, webhookUrl } = data?.data;
  const handleViewWebhook = () => {
    setShowWebhook(true);
  };

  const handleClose = () => setShowWebhook(false);

  return type === API_TYPES.ASYNC ? (
    <>
      {!webhookUrl || webhookUrl === DUMMY_WEBHOOK_URL ? (
        <>
          <TextButton
            className="add-webhook-button"
            icon={<StyledIconStatusWarningExclamation />}
            text={WEBHOOK_BUTTON_TEXT}
            onClick={handleViewWebhook}
            isDisabled={isLoadingTableData}
          />
        </>
      ) : (
        <TextButton
          className="link-renderer text-wrap"
          onClick={handleViewWebhook}
          isDisabled={isLoadingTableData}
          text={webhookUrl}
        />
      )}
      <APIWebhook
        data={data?.data}
        isOpen={showWebhook}
        handleClose={handleClose}
      />
    </>
  ) : (
    <TextButton isDisabled={true} text={NA} />
  );
};

export const ActionMenuRenderer = ({
  gridParams,
  setShowEditPlan,
  setSelectedAPIEditPlanContext,
  handleAddLocation,
  handleRemoveLocation,
  isLoadingTableData,
}) => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const { authenticationToken: token } = state?.loginDetailsSlice;

  const { activeApiList } = state?.orderAPISlice;
  const { apisObject, asyncApisObject } = state?.apiDirectorySlice;
  const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice
  );

  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState({});
  const [viewApiTerms, setViewApiTerms] = useState(false);
  const [viewDeleteApiDialog, setViewDeleteApiDialog] = useState(false);
  const targetRef = useRef();
  const action = {
    ADD_LOCATIONS: 'ADD_LOCATIONS',
    REMOVE_LOCATIONS: 'REMOVE_LOCATIONS',
    EDIT_PLAN: 'EDIT_PLAN',
    SEE_TERMS: 'SEE_TERMS',
    DELETE_API: 'DELETE_API',
  };
  let api = getMatchingResources(
    { apiType: gridParams?.data?.type, apiId: gridParams?.data?.id },
    apisObject,
    asyncApisObject,

  );
  const options = api?.isContracted
    ? [
        {
          label: 'Add Locations',
          action: action.ADD_LOCATIONS,
          icon: <IconAdd />,
        },
        {
          label: 'Remove Locations',
          action: action.REMOVE_LOCATIONS,
          icon: <IconRemove />,
        },
      ]
    : [
        {
          label: 'Add Locations',
          action: action.ADD_LOCATIONS,
          icon: <IconAdd />,
        },
        {
          label: 'Remove Locations',
          action: action.REMOVE_LOCATIONS,
          icon: <IconRemove />,
        },
        {
          label: 'Edit Pricing Plan',
          action: action.EDIT_PLAN,
          icon: <IconEdit />,
        },
        {
          label: 'View Terms',
          action: action.SEE_TERMS,
          icon: <IconVisibility />,
        },
        { label: 'Delete', action: action.DELETE_API, icon: <IconDelete /> },
      ];

  const handleClose = (event) => {
    setIsOpen(false);
    targetRef.current.focus();
  };
  const handleOpen = (event) => {
    setIsOpen(true);
  };

  const handleCloseApiTerms = () => {
    setViewApiTerms(false);
    setSelectedOption({});
    targetRef.current.focus();
  };

  const handleCloseDeleteApiDialog = () => {
    setViewDeleteApiDialog(false);
    setSelectedOption({});
    sendAmplitudeData(
      AMPLITUDE_EVENTS.DELETE_API_CONFIRMATION_CANCEL,
      orgAmplitudeProperties
    );
    targetRef.current.focus();
  };

  const handleSelect = (option) => {
    if (
      option.action === action.SEE_TERMS ||
      option.action === action.ADD_LOCATIONS ||
      option.action === action.REMOVE_LOCATIONS
    ) {
      trackManageApiIntegration({
        actionType: option.label,
        apiId: gridParams?.data?.id,
        apiDisplayName: gridParams?.data?.name,
        apiType: getFormattedApiType(gridParams?.data),
        isBundle: false,
      });
    }

    if (option.label === 'View Terms') {
      setViewApiTerms(true);
      sendAmplitudeData(
        AMPLITUDE_EVENTS.API_MENU_VIEW_TERMS,
        orgAmplitudeProperties
      );
    }
    setIsOpen(false);
    if (option.action === action.EDIT_PLAN) {
      dispatch(
        getAPIDetailsById({
          apiId: gridParams?.data?.id,
          apiType: gridParams?.data?.type,
          token,
        })
      );

      setSelectedAPIEditPlanContext({
        name: gridParams?.data?.name,
        id: gridParams?.data?.id,
        type: gridParams?.data?.type,
        integrations: gridParams?.data?.integrations,
        pricingPlans: gridParams?.data?.pricingPlans,
        consent: gridParams?.data?.consent,
      });

      setShowEditPlan(true);
      sendAmplitudeData(
        AMPLITUDE_EVENTS.API_MENU_EDIT_PRICING_PLAN,
        orgAmplitudeProperties
      );
    }
    if (option.action === action.ADD_LOCATIONS) {
      handleAddLocation(gridParams?.data);
      sendAmplitudeData(
        AMPLITUDE_EVENTS.API_MENU_ADD_LOCATIONS,
        orgAmplitudeProperties
      );
    }
    if (option.action === action.REMOVE_LOCATIONS) {
      handleRemoveLocation(gridParams?.data);
      sendAmplitudeData(
        AMPLITUDE_EVENTS.API_MENU_REMOVE_LOCATIONS,
        orgAmplitudeProperties
      );
    }
    if (option.action === action.DELETE_API) {
      setViewDeleteApiDialog(true);
      sendAmplitudeData(
        AMPLITUDE_EVENTS.API_MENU_DELETE_API,
        orgAmplitudeProperties
      );
    }
    setSelectedOption(option);
    targetRef.current.focus();
  };

  return (
    <>
      <div className="">
        <IconButton
          icon={<IconMoreVert />}
          text={'Action'}
          ref={targetRef}
          isDisabled={isLoadingTableData}
        />
        <Menu
          dataTestId="action-button-menu-container"
          labelRef={targetRef}
          onClose={handleClose}
          onOpen={handleOpen}
          isAuto={true}
        >
          {options.map((option, ind) => (
            <MenuListItem
              id={option.label}
              dataTestId={`menu-item-${option.label}`}
              key={`menu-${option.label}`}
              onClick={() => handleSelect(option)}
              graphicContent={option.icon}
              isDisabled={
                (!hasIntegrationCertified(gridParams?.data?.integrations) &&
                  option.label !== 'View Terms' &&
                  option.label !== 'Delete')
              }
            >
              {option.label}
            </MenuListItem>
          ))}
        </Menu>
      </div>
      {viewApiTerms && (
        <APITerms
          data={gridParams?.data}
          isOpen={viewApiTerms}
          handleClose={handleCloseApiTerms}
        />
      )}
      {viewDeleteApiDialog && (
        <APIDelete
          data={gridParams?.data}
          isOpen={viewDeleteApiDialog}
          handleClose={handleCloseDeleteApiDialog}
        />
      )}
    </>
  );
};

const APIIntegrationsTable = ({ isLoadingTableData, setApiCount }) => {
  const dispatch = useDispatch();
  const state = useSelector((state) => state);
  const [gridApi, setGridApi] = useState(null);
  const [tableData, setTableData] = useState([]);
  const { authenticationToken: token } = state?.loginDetailsSlice;
  const orgAmplitudeProperties = getOrgAmplitudeProperties(
    state?.loginDetailsSlice
  );
  const { apisObject, asyncApisObject } = state.apiDirectorySlice;
  const [showEditPlan, setShowEditPlan] = useState(false);
  const [selectedAPIEditPlanContext, setSelectedAPIEditPlanContext] = useState(
    {}
  );
  const [showWebhookWarningDialog, setShowWebhookWarningDialog] = useState(false);

  const {
    orders,
    consents,
  } = state?.orderAPISlice;
  const { restApis, asyncApis } = orders;

  const history = useHistory();

  useEffect(() => {
    if (!isLoadingTableData) {
      const consentsByAPIid =
        consents && groupBy(Object.values(consents), ({ api }) => api);

      if (restApis?.length || asyncApis?.length) {
        let appIntegrations = [...restApis, ...asyncApis];

        appIntegrations.sort((a, b) => {
          const apiName1 = a?.name?.toUpperCase();
          const apiName2 = b?.name?.toUpperCase();

          if (apiName1 < apiName2) {
            return -1;
          }
          if (apiName1 > apiName2) {
            return 1;
          }
          return 0;
        });

        const data = appIntegrations.map((f) => {
          const webhook =
            Array.isArray(f.integrations) &&
            f?.integrations?.length > 0 &&
            f?.integrations[0]?.webhookUrl;
          return {
            name: f.name,
            id: f.id,
            orgId: f.orgId,
            type: f.type,
            webhookUrl: webhook,
            integrations: f.integrations,
            pricingPlans: f.pricingPlans,
            consent:
              consentsByAPIid?.[
                f.type === 'api' ? `api-v2-${f.id}` : f.id
              ]?.[0] || {},
          };
        });
        setTableData(JSON.parse(JSON.stringify(data)));
        dispatch(storeActiveApiList(appIntegrations));
        setApiCount(restApis.length + asyncApis.length);
      }
    }
  }, [restApis, asyncApis, consents, isLoadingTableData]);

  const apiName = (gridParams) => {
    const { data } = gridParams;
    const api = getMatchingResources(
      { apiType: data?.type, apiId: data?.id },
      apisObject,
      asyncApisObject
    );
    const handleViewInMyAPIs = async () => {
      const apiId = data?.id;
      const apiType = data?.type;
      const location = {
        search: 'q=Specification',
      };
      try {
        const response = await dispatch(
          getAPIDetailsById({ apiId, apiType, token })
        ).unwrap();
        if (!response || (typeof response === 'object' && Object.keys(response).length === 0)) {
          console.error('API returned no data!');
          return;
        }
        dispatch(setAPIDirectoryPage({ page: 'apiDetails', props: { apiId, location, prevScreen: 'myApis' } }));
        history.push(API_DIR_URL);
      } catch (error) {
        console.error('API call failed:', error);
      }
    };

    const IconWithTooltip = withTooltip(
      (props) => (
        <IconSales {...props} className="api-integrations-contracted-apis" />
      ),
      {
        id: 'contract-icon',
        text: 'Contracted Off Platform',
        theme: THEMES.LIGHT,
      }
    );

    const { name } = data;
    return (
      <>
        <TextButton
          className="link-renderer text-wrap"
          text={name}
          onClick={handleViewInMyAPIs}
          isDisabled={isLoadingTableData}
        ></TextButton>
        {api?.isContracted && (
          <IconWithTooltip
            horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.MIDDLE}
            marginAroundElement={2}
            verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.MIDDLE}
          />
        )}
      </>
    );
  };

  const customActionBtnRenderer = (gridParams) => {
    return (
      <ActionMenuRenderer
        gridParams={gridParams}
        setShowEditPlan={setShowEditPlan}
        setSelectedAPIEditPlanContext={setSelectedAPIEditPlanContext}
        handleAddLocation={handleAddLocation}
        handleRemoveLocation={handleRemoveLocation}
        isLoadingTableData={isLoadingTableData}
      />
    );
  };

  const LocationsRender = ({ data }) => {
    const state = useSelector((state) => state);
    const orgAmplitudeProperties = getOrgAmplitudeProperties(
      state?.loginDetailsSlice
    );
    const { id, type, webhookUrl, name, orgId } = data?.data;
    const ROW_TEXT = `View Locations`;
    return (
      <TextButton
        data-testid={`manage-location-${id}`}
        className={'link-renderer text-wrap'}
        text={ROW_TEXT}
        onClick={() => {
          dispatch(clearSubscriptions());
          dispatch(
            setScreen({
              screen: screens.VIEW_LOCATIONS,
              additionalData: { id, type, webhookUrl, name, orgId },
            })
          );
          sendAmplitudeData(
            AMPLITUDE_EVENTS.MY_APIS_VIEW_LOCATIONS,
            orgAmplitudeProperties
          );
        }}
        isDisabled={isLoadingTableData}
      ></TextButton>
    );
  };

  const columns = [
    {
      cellRendererFramework: apiName,
      field: 'api',
      headerName: 'Name',
      hide: false,
    },
    {
      cellRendererFramework: (props) => (
        <div className="api-name text-wrap">
          {props.data.type === API_TYPES.REST ? (
            <TextButton
              className={`text-renderer text-wrap ${
                !isLoadingTableData ? 'api-type-button' : ''
              }`}
              dataTestId="api-type-rest"
              text={API_TYPES_OPTIONS.REST}
              isDisabled={isLoadingTableData}
            ></TextButton>
          ) : props.data.type === API_TYPES.ASYNC ? (
            <TextButton
              className={`text-renderer text-wrap ${
                !isLoadingTableData ? 'api-type-button' : ''
              }`}
              dataTestId="api-type-async"
              text={API_TYPES_OPTIONS.ASYNC}
              isDisabled={isLoadingTableData}
            ></TextButton>
          ) : (
            ''
          )}
        </div>
      ),
      field: '',
      headerName: 'Type',
      minWidth: 120,
      width: 120,
      hide: false,
    },
    {
      cellRendererFramework: (props) => {
        let integrations = props?.data?.integrations;
        if (hasIntegrationCertified(integrations)) {
          return <LocationsRender data={props} />;
        } else {
          return (
            <div dataTestId="pending-review" className={`pending-review`}>
              Pending CDK Review
            </div>
          );
        }
      },
      field: '',
      headerName: 'Locations',
      minWidth: 120,
      width: 120,
      hide: false,
    },
    {
      cellRendererFramework: (props) => (
        <WebhookRender data={props} isLoadingTableData={isLoadingTableData} />
      ),
      field: '',
      headerName: 'Webhook URL',
      minWidth: 120,
      width: 120,
      hide: false,
    },
    {
      cellRendererFramework: customActionBtnRenderer,
      field: '',
      headerName: '',
      minWidth: 30,
      width: 30,
      hide: false,
    },
  ];

  const handleGridReady = (gridParams) => {
    setGridApi(gridParams.api);
  };

  const handleAddLocation = (apiData) => {
    if(apiData?.type === RESOURCE_TYPES.ASYNC_API && (!apiData?.integrations?.[0]?.webhookUrl || apiData?.integrations?.[0]?.webhookUrl === DUMMY_WEBHOOK_URL)) {
      setShowWebhookWarningDialog(true);
    } else {
      dispatch(
        setScreen({
          screen: screens.DAAB_ADD_LOCATIONS,
          solutionData: orders?.app,
          additionalData: apiData,
        })
      );
    }
  };
  const handleRemoveLocation = (apiData) => {
    dispatch(
      setScreen({
        screen: screens.DAAB_REMOVE_LOCATIONS,
        solutionData: orders?.app,
        additionalData: apiData,
      })
    );
  };

  return (
    <>
      <div data-testid="root-div">
        <>
          <div
            id="ag-grid-container"
            className={classNames(
              'api-integration-table-container',
              'ag-theme-balham'
            )}
          >
            <Table
              key={`api-table-${isLoadingTableData}`}
              columns={columns}
              data={tableData}
              rowHeight={52}
              isSearchable={false}
              isDownloadable={false}
              isPrintable={false}
              resizableHeaders={true}
              showNoRowsOverlay={false}
              showFooter={false}
              hideTableTitle={true}
              hideBorder={true}
              onGridReady={handleGridReady}
              data-testid="api-integration-table"
              hasPagination={false}
              tableLayout={TABLE_LAYOUT_OPTIONS.AUTO_HEIGHT}
              id="api-integration-table"
              headerTitle=""
              searchInputName=""
              frameworkComponents={{
                customNoRowsOverlay: (params) => (
                  <NoDataOverlay message="There are no APIs to show." />
                ),
              }}
              noRowsOverlayComponent="customNoRowsOverlay"
            />
          </div>
          <APIPricingPlan
            title="Edit Pricing Plan"
            apiId={selectedAPIEditPlanContext?.id}
            isOpen={showEditPlan}
            onClose={() => {
              setShowEditPlan(false);
              sendAmplitudeData(
                AMPLITUDE_EVENTS.MY_APIS_EDIT_PRICING_PLAN_CANCEL,
                orgAmplitudeProperties
              );
            }}
            onAccept={() => {
              setShowEditPlan(false);
            }}
            selectedAPIEditPlanContext={selectedAPIEditPlanContext}
            pageTitle={PAGE_TITLE.MY_APIS}
          />
          <WebhookWarningDialog
            isOpen={showWebhookWarningDialog}
            onClose={() => setShowWebhookWarningDialog(false)}
          />
        </>
      </div>
    </>
  );
};

export default APIIntegrationsTable;